import type { FC } from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Margin, styled, useIsDarkMode } from "@kivra/react-components";
import { StrongAuthService } from "../atoms/StrongAuthService";
import { ErrorMessage } from "../errors/ErrorMessage";
import { FadeInAndOut } from "../atoms/FadeInAndOut";
import {
  getPasswordResetPath,
  getPathname,
  getTunnistusPath,
  redirect,
} from "../../utils/location";
import {
  getStrongAuthServicesSorted,
  strongAuthUrl,
} from "../../utils/teliaTunnistus";
import type { StrongAuthService as StrongAuthServiceType } from "../../utils/teliaTunnistus";
import {
  convertUrlParametersToBase64,
  generateNonce,
} from "../../utils/helpers";
import { getHideNavigation } from "../../store/hideNavigation/hideNavigation";
import { KivraHeaderRouter } from "../header/KivraHeaderRouter";
import { routes } from "../../utils/routes";
import { getTermsAccepted } from "../../store/auth";
import { getRedirectParameters } from "../../@api/config";

interface StrongAuthServiceWithClick extends StrongAuthServiceType {
  strongAuthClick: () => void;
}

interface StrongAuthActions {
  strongAuthServices: StrongAuthServiceWithClick[];
}

export const StrongAuthSelection: FC = () => {
  const { strongAuthServices } = useStrongAuthActions();
  const [clickedService, setClickedService] = useState<string>();
  return (
    <div style={{ minWidth: "250px" }}>
      <KivraHeaderRouter />
      <ErrorMessage />
      <FadeInAndOut>
        <Margin bottom={16} />
        <ServicesContainer>
          {strongAuthServices.map((service) => (
            <StrongAuthService
              key={service.id}
              clickedService={clickedService}
              setClickedService={setClickedService}
              {...service}
            />
          ))}
        </ServicesContainer>
      </FadeInAndOut>
    </div>
  );
};

const ServicesContainer = styled.div({
  display: "grid",
  gridGap: "8px",
  justifyContent: "center",
  gridTemplateColumns: "1fr 1fr 1fr",
  marginTop: "$spacing-24",
  $small: {
    gridTemplateColumns: "repeat(auto-fill, 109px)",
  },
});

const useStrongAuthActions = (): StrongAuthActions => {
  const traceId = Math.floor(Math.random() * 10000);
  const services = getStrongAuthServicesSorted();
  const colorScheme = useIsDarkMode() ? "dark" : "light";
  const termsAccepted = useSelector(getTermsAccepted);
  const authContext = getPathname(window.location);
  const redirectPath = authContext === routes.PASSWORD_RESET
    ? getPasswordResetPath(window.location)
    : getTunnistusPath(window.location);

  return {
    strongAuthServices: services.map((service) => ({
      ...service,
      strongAuthClick: () => {
        redirect(
          strongAuthUrl(
            service.id,
            generateNonce(),
            redirectPath,
            convertUrlParametersToBase64({
              redirectParameters: getRedirectParameters(),
              colorScheme,
              hideNavigation: getHideNavigation(),
              traceString:
                `${service.id} agent: ${window.navigator.userAgent} @ ${
                  new Date().toUTCString()
                }`,
              traceId,
              termsAccepted,
              authContext,
            }),
          ),
        );
      },
    })),
  };
};
